import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '../components/ui/dialog';
import { 
  Plane, 
  Plus, 
  FileText, 
  Loader2,
  Pencil,
  Trash2,
  Calendar,
  User,
  Clock,
  Eye,
  EyeOff,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import NewsForm from '../components/NewsForm';
import Sidebar from '../components/Sidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, newsId: null });
  const [deleting, setDeleting] = useState(false);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const fetchNews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const handleLogout = () => {
    navigate('/');
  };

  const handleAddNews = () => {
    setEditingNews(null);
    setShowForm(true);
  };

  const handleEditNews = (newsItem) => {
    setEditingNews(newsItem);
    setShowForm(true);
  };

  const handleDeleteClick = (newsId) => {
    setDeleteDialog({ open: true, newsId });
  };

  const handleToggleActive = async (newsId, currentActive) => {
    try {
      await axios.patch(`${API}/news/${newsId}/toggle-active`);
      setNews(prev => prev.map(n => n.id === newsId ? { ...n, active: !currentActive } : n));
      toast.success(language === 'es'
        ? (!currentActive ? 'Noticia activada' : 'Noticia desactivada')
        : (!currentActive ? 'News activated' : 'News deactivated'));
    } catch {
      toast.error(t('errorOccurred'));
    }
  };

  const handleDeleteConfirm = async () => {    setDeleting(true);
    try {
      await axios.delete(`${API}/news/${deleteDialog.newsId}`);
      toast.success(t('newsDeleted'));
      fetchNews();
    } catch (error) {
      console.error('Error deleting news:', error);
      toast.error(t('errorOccurred'));
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, newsId: null });
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingNews(null);
    fetchNews();
  };

  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
    if (dragItem.current === null || dragItem.current === index) return;
    setNews(prev => {
      const updated = [...prev];
      const dragged = updated.splice(dragItem.current, 1)[0];
      updated.splice(index, 0, dragged);
      dragItem.current = index;
      return updated;
    });
  };

  const handleDragEnd = async () => {
    dragItem.current = null;
    dragOverItem.current = null;
    try {
      const reorderPayload = news.map((item, idx) => ({ id: item.id, order: idx }));
      await axios.patch(`${API}/news/reorder`, reorderPayload);
    } catch {
      toast.error(t('errorOccurred'));
      fetchNews();
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (showForm) {
    return (
      <NewsForm 
        news={editingNews} 
        onSuccess={handleFormSuccess} 
        onCancel={() => { setShowForm(false); setEditingNews(null); }} 
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 lg:mb-8">
            <div>
              <h1 className="font-barlow text-2xl lg:text-3xl font-bold uppercase tracking-tight text-[#1E3A5F]">
                {t('newsManagement')}
              </h1>
              <p className="text-slate-500 mt-1 text-sm">
                {language === 'es' 
                  ? `${news.length} noticias registradas`
                  : `${news.length} news items registered`}
              </p>
            </div>
            <Button
              onClick={handleAddNews}
              className="bg-[#8B1538] hover:bg-[#6D102B] text-white font-medium uppercase tracking-wide gap-2"
              data-testid="add-news-button"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">{t('addNews')}</span>
            </Button>
          </div>

          {/* Drag-and-drop info banner */}
          {news.length > 1 && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#1E3A5F]/20 bg-[#1E3A5F]/5 px-4 py-2 text-sm text-[#1E3A5F]">
              <GripVertical className="h-4 w-4 flex-shrink-0 opacity-60" />
              <span>
                {language === 'es'
                  ? 'Arrastra las noticias para cambiar el orden. El orden aquí se refleja en la app móvil.'
                  : 'Drag news items to reorder. The order here is reflected in the mobile app.'}
              </span>
            </div>
          )}

          {/* News List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
            </div>
          ) : news.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <FileText className="h-16 w-16 text-slate-300 mb-4" />
                <p className="text-slate-500 text-lg">{t('noNews')}</p>
                <Button
                  onClick={handleAddNews}
                  className="mt-4 bg-[#8B1538] hover:bg-[#6D102B] text-white"
                  data-testid="add-first-news-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('addNews')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {news.map((item, index) => (
                <Card 
                  key={item.id} 
                  className={`border-slate-200 hover:shadow-md transition-shadow overflow-hidden ${item.active === false ? 'opacity-60' : ''}`}
                  data-testid={`news-card-${item.id}`}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragEnter={() => handleDragEnter(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <div className="flex flex-col sm:flex-row">
                    {/* Drag handle */}
                    <div className="hidden sm:flex items-center px-2 text-slate-300 cursor-grab active:cursor-grabbing">
                      <GripVertical className="h-5 w-5" />
                    </div>
                    {/* Background Image Preview */}
                    <div className="w-full sm:w-48 h-40 flex-shrink-0 relative bg-slate-100">
                      <img 
                        src={item.backgroundImageUrl.startsWith('/api') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${item.backgroundImageUrl}`
                          : item.backgroundImageUrl
                        }
                        alt={item.title[language] || item.title.es}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1767868280782-fc108d087050?crop=entropy&cs=srgb&fm=jpg&q=85';
                        }}
                      />
                      {item.portraitImageUrl && (
                      <div className="absolute bottom-2 right-2 w-12 h-12 rounded-full border-2 border-white overflow-hidden shadow-lg bg-white">
                        <img 
                          src={item.portraitImageUrl.startsWith('/api')
                            ? `${process.env.REACT_APP_BACKEND_URL}${item.portraitImageUrl}`
                            : item.portraitImageUrl
                          }
                          alt="Portrait"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.parentElement.style.display = 'none';
                          }}
                        />
                      </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <span className="inline-flex items-center rounded-md bg-[#1E3A5F]/10 px-2 py-1 text-xs font-medium text-[#1E3A5F] border border-[#1E3A5F]/20 mb-2">
                            {item.badge[language] || item.badge.es}
                          </span>
                          {item.active === false && (
                            <span className="ml-2 inline-flex items-center rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-500 border border-slate-200 mb-2">
                              {language === 'es' ? 'Inactiva' : 'Inactive'}
                            </span>
                          )}
                          <h3 className="font-barlow text-base lg:text-lg font-semibold text-[#1E3A5F] uppercase tracking-tight truncate">
                            {item.title[language] || item.title.es}
                          </h3>
                          <p className="text-sm text-slate-500 truncate">
                            {item.subtitle[language] || item.subtitle.es}
                          </p>
                          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-400">
                            <span className="flex items-center gap-1">
                              <Plane className="h-3 w-3" />
                              {item.frequency[language] || item.frequency.es}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(item.publishedAt)}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-400">
                            {item.createdBy && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {item.createdBy}
                              </span>
                            )}
                            {item.createdAt && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {language === 'es' ? 'Creado' : 'Created'}: {formatDate(item.createdAt)}
                              </span>
                            )}
                            {item.updatedAt && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {language === 'es' ? 'Actualizado' : 'Updated'}: {formatDate(item.updatedAt)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleActive(item.id, item.active !== false)}
                            className={item.active === false
                              ? 'text-slate-400 border-slate-200 hover:bg-green-50 hover:text-green-600 hover:border-green-200'
                              : 'text-green-600 border-slate-200 hover:bg-slate-50'}
                            title={item.active === false
                              ? (language === 'es' ? 'Activar' : 'Activate')
                              : (language === 'es' ? 'Desactivar' : 'Deactivate')}
                          >
                            {item.active === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditNews(item)}
                            className="text-[#1E3A5F] border-slate-200 hover:bg-slate-50"
                            data-testid={`edit-news-${item.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClick(item.id)}
                            className="text-red-600 border-slate-200 hover:bg-red-50 hover:border-red-200"
                            data-testid={`delete-news-${item.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                        {item.description[language] || item.description.es}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, newsId: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F] font-barlow uppercase tracking-tight">
              {t('deleteNews')}
            </DialogTitle>
            <DialogDescription>
              {t('confirmDelete')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, newsId: null })}
              disabled={deleting}
              data-testid="cancel-delete-button"
            >
              {t('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-button"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                t('deleteNews')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
